import React, { useCallback, useMemo, useState } from "react";
import * as Dfns from "date-fns/fp";
import * as R from "ramda";
import { MdSave } from "react-icons/md";
import "./FiscalCalendarInputPage.scss";
import {
  calPatternsMap,
  CompanyFiscalCal,
  FiscalCalType,
  genPatternBasedCal,
  getMonthName,
  isParseable,
  PatternType,
} from "@blisspointmedia/bpm-types/dist/FiscalCalendar";
import { DATE_FORMAT } from "@blisspointmedia/bpm-types/dist/RelativeDatePicker";
import {
  CAL_TYPES_RADIO_OPTIONS,
  EXTRA_WEEK_TIPS,
  EXTRA_WEEK_YEAR_OPTIONS,
  MONTH_LABEL_CURRENT_QUESTION,
  NESTED_QUESTION_CONFIG,
  OVERWRITE_NOTE,
  PATTERN_QUESTION,
  WEEK_DEFINITION_NOTE,
  WEEK_DEFINITION_OPTIONS,
  YEAR_LABEL_CURRENT_QUESTION,
} from "./fiscalCalConstants";
import { SetFiscalCal, useCompanyInfo } from "../redux/company";
import {
  Button,
  ButtonFrameworkVariant,
  ButtonType,
  DropdownToggleType,
  Page,
  Spinner,
} from "../Components";
import ChartContainer from "../Components/ChartContainer";
import { QuestionCard } from "./QuestionCard";
import { OnChangeHelper, Question, QuestionCardType } from "./Question";
import { NestedInputType, NestedOptionConfig, NestedOptionsConfigMap } from "./NestedInput";
import { AdminLambdaFetch, ClientToolsLambdaFetch } from "../utils/fetch-utils";
import { useSetAreYouSure, useSetError } from "../redux/modals";
import { useDispatch } from "react-redux";

export interface FiscalCalFormState extends Omit<CompanyFiscalCal, "seedDate"> {
  seedDate: string | number | null;
}

const startOfWeekCheck = start => !R.isNil(start) && start >= 0 && start < 7;
const seedDateCheckYearly = date =>
  !R.isNil(date) && typeof date === "number" ? date > 0 && date < 12 : isDateString(date);
const isDateString = date => !R.isNil(date) && typeof date == "string" && date.length === 10;
const disableYearCurrentQuestion = (monthCurrent, seedDate) =>
  !R.isNil(seedDate) && monthCurrent === false && Dfns.getMonth(Dfns.parseISO(seedDate)) === 11;
const extraWeekOnChangeHelper = (val: string, state: FiscalCalFormState): FiscalCalFormState => {
  if (!state.extraWeekYear) {
    state.extraWeekYear = {};
  }
  if (state.extraWeekYear[val] === undefined) {
    state.extraWeekYear[val] = 11;
  } else {
    delete state.extraWeekYear[val];
  }
  return state;
};
const allEqualAndNotNil = (obj?: { [key: string]: number } | null) => {
  if (!obj || R.values(obj).length === 0) {
    return false;
  }
  const values = new Set(R.values(obj).filter(val => !R.isNil(val)));
  return values.size === 1;
};
const getWeekDeffNote = (formData: Partial<FiscalCalFormState>) =>
  formData.calType === FiscalCalType.PATTERN ? WEEK_DEFINITION_NOTE : undefined;

export const FiscalCalendarInputPage: React.FC<any> = () => {
  const { fiscal_cal: fiscalCal, cid } = useCompanyInfo();
  const [saving, setSaving] = useState(false);
  const setAreYouSure = useSetAreYouSure(true);
  const setError = useSetError();
  const dispatch = useDispatch();
  const [extendExtraWeeks, setExtendExtraWeeks] = useState(
    fiscalCal &&
      fiscalCal.calType === FiscalCalType.PATTERN &&
      !allEqualAndNotNil(fiscalCal.extraWeekYear)
  );
  const [fiscalCalFormData, setFiscalCalFormData] = useState<Partial<FiscalCalFormState>>(
    fiscalCal && fiscalCal.calType
      ? {
          ...fiscalCal,
          seedDate:
            fiscalCal.calType === FiscalCalType.YEARLY && fiscalCal.seedDate
              ? Dfns.getMonth(Dfns.parseISO(fiscalCal.seedDate))
              : fiscalCal.seedDate,
        }
      : {}
  );

  const updateFiscalCalSettingsState = useCallback((value, path, helper) => {
    setFiscalCalFormData(f => {
      const currCaltype = path === "calType" && value ? value : f.calType;

      if (currCaltype === FiscalCalType.YEARLY && f.calType !== FiscalCalType.YEARLY) {
        return R.clone({ ...f, calType: currCaltype, seedDate: 1 });
      } else if (currCaltype === FiscalCalType.PATTERN && f.calType !== FiscalCalType.PATTERN) {
        return R.clone({
          ...f,
          calType: currCaltype,
          pattern: PatternType.PATTERN_A,
          yearLabelCurrent: true,
          monthLabelCurrent: true,
          startOfWeek: null,
          extraWeekYear: {},
        });
      } else if (
        currCaltype === FiscalCalType.PATTERN &&
        path === "monthLabelCurrent" &&
        disableYearCurrentQuestion(value, f.seedDate)
      ) {
        // overwrite year month current
        return R.clone({ ...f, yearLabelCurrent: false, [path]: value });
      } else if (currCaltype === FiscalCalType.PATTERN && path === "seedDate") {
        const isParseableDate =
          typeof value === "string" && value.length === 10 && isParseable(value);
        const startOfWeek = isParseableDate ? Dfns.getDay(Dfns.parseISO(value)) : null;
        return R.clone({ ...f, startOfWeek: startOfWeek, [path]: value });
      }
      if (helper) {
        return helper(value, R.clone(f));
      }
      return R.clone({ ...f, [path]: value });
    });
  }, []);

  const isUpdate = useMemo(() => !R.isNil(fiscalCal) && !R.isNil(fiscalCal.calType), [fiscalCal]);
  const updateDiffPresent = useMemo(() => {
    if (isUpdate && !R.isNil(fiscalCal)) {
      for (const key of R.keys(fiscalCal)) {
        if (
          fiscalCal.calType === FiscalCalType.YEARLY &&
          key === "seedDate" &&
          typeof fiscalCal[key] === "string" &&
          typeof fiscalCalFormData[key] === "number"
        ) {
          if (fiscalCalFormData[key] !== Dfns.getMonth(Dfns.parseISO(fiscalCal[key]))) {
            return true;
          }
        } else if (fiscalCal.calType === FiscalCalType.PATTERN && key === "startOfWeek") {
          continue;
        } else if (!R.equals(fiscalCal[key], fiscalCalFormData[key])) {
          return true;
        }
      }
    }
    return false;
  }, [fiscalCal, isUpdate, fiscalCalFormData]);

  const formIsValid = useMemo(() => {
    const {
      calType,
      pattern,
      startOfWeek,
      monthLabelCurrent,
      yearLabelCurrent,
      seedDate,
    } = fiscalCalFormData;
    if (calType === FiscalCalType.TRADITIONAL) {
      return startOfWeekCheck(startOfWeek);
    } else if (calType === FiscalCalType.YEARLY) {
      return startOfWeekCheck(startOfWeek) && seedDateCheckYearly(seedDate);
    } else if (calType === FiscalCalType.PATTERN) {
      // currently not validating 53 week years
      return (
        !R.isNil(pattern) &&
        !R.isNil(monthLabelCurrent) &&
        !R.isNil(yearLabelCurrent) &&
        isDateString(seedDate)
      );
    } else {
      return false;
    }
  }, [fiscalCalFormData]);

  const saveFiscalCalSettings = useCallback(() => {
    (async () => {
      try {
        setSaving(true);
        const {
          calType,
          startOfWeek,
          extraWeekYear,
          monthLabelCurrent,
          yearLabelCurrent,
          seedDate,
          pattern,
        } = fiscalCalFormData;
        const formattedSeedDate =
          calType === FiscalCalType.YEARLY && typeof seedDate === "number"
            ? Dfns.format(DATE_FORMAT, new Date(2025, seedDate, 1))
            : seedDate;
        const body = {
          company: cid,
          pattern,
          calType,
          yearLabelCurrent,
          monthLabelCurrent,
          seedDate: formattedSeedDate,
          startOfWeek,
          extraWeekYear,
        };
        await ClientToolsLambdaFetch("/saveFiscalCalendarInputs", {
          method: "POST",
          body: body,
        });
        let newSavedInfo;
        switch (calType) {
          case FiscalCalType.TRADITIONAL: {
            newSavedInfo = { calType, startOfWeek };
            break;
          }
          case FiscalCalType.YEARLY: {
            newSavedInfo = { calType, startOfWeek, seedDate: formattedSeedDate };
            break;
          }
          case FiscalCalType.PATTERN: {
            newSavedInfo = {
              calType,
              extraWeekYear,
              monthLabelCurrent,
              yearLabelCurrent,
              seedDate,
              pattern,
              startOfWeek: Dfns.getDay(Dfns.parseISO(seedDate)),
            };
            newSavedInfo.generatedPatternCal = genPatternBasedCal(newSavedInfo);
          }
        }
        dispatch(SetFiscalCal(cid, newSavedInfo));
        try {
          await AdminLambdaFetch("/clearField", {
            method: "DELETE",
            params: {
              field: cid,
              key: "companyInfo",
            },
          });
        } catch (e) {
          const err = e as Error;
          console.error(`Error clearing company info cache, company: ${cid}`, err.message);
        }
        setError({
          variant: "success",
          title: "Success!",
          message: "Success saving fiscal calendar settings.",
        });
      } catch (e) {
        const err = e as Error;
        setError({
          title: "Internal Error",
          message: `Error saving fiscal calendar ${err.message}`,
        });
      } finally {
        setSaving(false);
      }
    })();
  }, [fiscalCalFormData, cid, setError, dispatch]);

  const nestedQuestionConfigMap = useMemo(() => {
    const configToUse = { ...NESTED_QUESTION_CONFIG } as NestedOptionsConfigMap;
    const currVal = fiscalCalFormData.seedDate;
    const isParseableDate = typeof currVal === "string" && currVal.length === 10;

    if (fiscalCalFormData.calType === FiscalCalType.PATTERN) {
      const patternQuestion = {
        ...PATTERN_QUESTION,
        onChangeHelper: ((value: PatternType, state: FiscalCalFormState): FiscalCalFormState => {
          if (value === state.pattern) {
            return state;
          }
          state.pattern = value;
          state.extraWeekYear = {};
          return state;
        }) as OnChangeHelper,
      };
      if (isParseableDate) {
        configToUse[FiscalCalType.PATTERN] = [
          ...NESTED_QUESTION_CONFIG[FiscalCalType.PATTERN],
          {
            ...MONTH_LABEL_CURRENT_QUESTION,
            options: Array.from({ length: 12 })
              .map((_, monthIndex) => {
                const monthLabelInd = isParseableDate
                  ? Dfns.getMonth(Dfns.addMonths(monthIndex, Dfns.parseISO(currVal)))
                  : -1;
                const monthName = isParseableDate ? getMonthName(monthLabelInd) : "--";

                return {
                  value:
                    monthIndex === 0
                      ? true
                      : monthIndex === 1
                      ? false
                      : `disabledMonth${monthIndex}`,
                  label: monthName,
                  disabled: monthIndex > 1,
                  labelInd: monthLabelInd,
                };
              })
              .sort((a, b) => a.labelInd - b.labelInd),
          },
          {
            ...YEAR_LABEL_CURRENT_QUESTION,
            options: [
              {
                value: true,
                label: isParseableDate ? `${Dfns.getYear(Dfns.parseISO(currVal))}` : "--",
              },
              {
                value: false,
                label: isParseableDate
                  ? `${Dfns.getYear(Dfns.addYears(1, Dfns.parseISO(currVal)))}`
                  : "--",
              },
            ],
            disable: disableYearCurrentQuestion(
              fiscalCalFormData.monthLabelCurrent,
              fiscalCalFormData.seedDate
            ),
          },
          patternQuestion,
        ] as NestedOptionConfig[];
      } else {
        configToUse[FiscalCalType.PATTERN] = [
          ...NESTED_QUESTION_CONFIG[FiscalCalType.PATTERN],
          {
            ...MONTH_LABEL_CURRENT_QUESTION,
            options: [],
            disable: true,
          },
          {
            ...YEAR_LABEL_CURRENT_QUESTION,
            options: [],
            disable: true,
          },
          { ...patternQuestion, disable: true },
        ] as NestedOptionConfig[];
      }
    }

    return configToUse;
  }, [fiscalCalFormData]);

  const patternActiveSeedComplete = useMemo(() => {
    const currVal = fiscalCalFormData.seedDate;
    const isParseableDate = typeof currVal === "string" && currVal.length === 10;
    const isPattern = fiscalCalFormData.calType === FiscalCalType.PATTERN;
    const lableQuestionsComplete =
      fiscalCalFormData.monthLabelCurrent !== undefined &&
      fiscalCalFormData.yearLabelCurrent !== undefined;
    return isPattern && isParseableDate && lableQuestionsComplete;
  }, [fiscalCalFormData]);

  return (
    <div className="fiscalCalendarInputPage">
      <Page title="Calendar Inputs" pageType="Calendar Inputs">
        <ChartContainer
          title={"Configure Your Calendar"}
          rightActions={
            <>
              {isUpdate && updateDiffPresent && (
                <Button
                  type={ButtonType.FILLED}
                  design="secondary"
                  variant={ButtonFrameworkVariant.NO_ICON}
                  onClick={() => {
                    setFiscalCalFormData(
                      fiscalCal && fiscalCal.calType
                        ? {
                            ...fiscalCal,
                            seedDate:
                              fiscalCal.calType === FiscalCalType.YEARLY && fiscalCal.seedDate
                                ? Dfns.getMonth(Dfns.parseISO(fiscalCal.seedDate))
                                : fiscalCal.seedDate,
                          }
                        : {}
                    );
                    setExtendExtraWeeks(
                      fiscalCal &&
                        fiscalCal.calType === FiscalCalType.PATTERN &&
                        !allEqualAndNotNil(fiscalCal.extraWeekYear)
                    );
                  }}
                  disabled={!updateDiffPresent}
                >
                  {"Discard Changes"}
                </Button>
              )}
              {
                <Button
                  type={ButtonType.FILLED}
                  variant={ButtonFrameworkVariant.LEADING_ICON}
                  icon={saving ? <Spinner /> : <MdSave />}
                  onClick={
                    isUpdate
                      ? async () => {
                          try {
                            await setAreYouSure({
                              title: "Overwrite current calendar settings?",
                              message: OVERWRITE_NOTE,
                              cancelText: "Cancel",
                              okayText: "Overwrite",
                              onOkay: () => {
                                saveFiscalCalSettings();
                              },
                            });
                          } catch {}
                        }
                      : saveFiscalCalSettings
                  }
                  disabled={saving || !formIsValid || (isUpdate && !updateDiffPresent)}
                >
                  {"Save Calendar"}
                </Button>
              }
            </>
          }
        >
          <QuestionCard questionNumber="1">
            <div className="calTypeQuestion">
              <Question
                title="What type of calendar do you use?"
                type={QuestionCardType.RADIO}
                subtitle="This determines how your weeks and months are displayed in the app."
                options={CAL_TYPES_RADIO_OPTIONS}
                nestedOptionsConfig={nestedQuestionConfigMap as NestedOptionsConfigMap}
                onChange={updateFiscalCalSettingsState}
                formState={fiscalCalFormData}
                formStateKey={"calType"}
              />
              {fiscalCalFormData.calType === FiscalCalType.PATTERN && (
                <div className="patternPreview">
                  <div className="patternPreviewLabel title">{"Pattern Preview"}</div>
                  <div className="patternPreviewRow">
                    <div className="spacer"></div>
                    <div className="previewRowItem monthName">{"Month Name"}</div>
                    <div className="previewRowItem numWeeks">{"# of Weeks"}</div>
                  </div>
                  {patternActiveSeedComplete ? (
                    Array.from({ length: 12 }).map((_, monthIndex) => (
                      <div
                        key={`patternPreviewRow${monthIndex}`}
                        className={`patternPreviewRow ${monthIndex % 2 === 0 ? "stripeRow" : ""}`}
                      >
                        <div className="patternPreviewLabel monthCount">{`${monthIndex + 1}.`}</div>
                        <div className="previewRowItem">
                          {getMonthName(
                            Dfns.getMonth(
                              Dfns.addMonths(
                                monthIndex + (fiscalCalFormData.monthLabelCurrent ? 0 : 1),
                                Dfns.parseISO(fiscalCalFormData.seedDate)
                              )
                            )
                          )}
                        </div>
                        <div className="previewRowItem">
                          {
                            calPatternsMap[fiscalCalFormData.pattern || PatternType.PATTERN_A][
                              monthIndex % 3
                            ]
                          }
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="patternPreviewPlaceholder">
                      {"Complete all of the above questions to see a preview."}
                    </div>
                  )}
                  <div className="patternPreviewLabel previewNote">
                    {
                      "Note: This shows a standard year with 52 weeks, please indicate your 53 week years in question 3."
                    }
                  </div>
                </div>
              )}
            </div>
          </QuestionCard>
          <QuestionCard questionNumber="2" hide={R.isNil(fiscalCalFormData.calType)}>
            <div className="calTypeQuestion">
              <Question
                title="How does your company define a week?"
                type={QuestionCardType.RADIO}
                options={WEEK_DEFINITION_OPTIONS}
                onChange={updateFiscalCalSettingsState}
                formState={fiscalCalFormData}
                formStateKey={"startOfWeek"}
                readOnly={fiscalCalFormData.calType === FiscalCalType.PATTERN}
                note={getWeekDeffNote(fiscalCalFormData)}
              />
            </div>
          </QuestionCard>
          <QuestionCard
            questionNumber="3"
            hide={fiscalCalFormData.calType !== FiscalCalType.PATTERN}
          >
            <div className="additionalPatternQuestions">
              <div>
                <Question
                  title="How do you handle 53-week years?"
                  type={QuestionCardType.RADIO}
                  readOnly={!patternActiveSeedComplete}
                  options={[
                    { label: "Same month always gets the extra week", value: false },
                    {
                      label: "Different months get the extra week in different years",
                      value: true,
                    },
                  ]}
                  onChange={value => {
                    setExtendExtraWeeks(value);
                    if (!value) {
                      setFiscalCalFormData(f => ({
                        ...f,
                        extraWeekYear: {},
                      }));
                    }
                  }}
                  formState={extendExtraWeeks}
                  formStateKey={null}
                  nestedOptionsConfig={{
                    false: [
                      {
                        inputType: NestedInputType.DROPDOWN_MULTI,
                        label: "All past and future fiscal years with a 53rd week:",
                        dropdownProps: {
                          type: DropdownToggleType.OUTLINED,
                          design: "primary",
                          itemOnClick: year => {
                            updateFiscalCalSettingsState(year, "", extraWeekOnChangeHelper);
                          },
                        },
                        options: EXTRA_WEEK_YEAR_OPTIONS.map(year => ({
                          ...year,
                          selected:
                            fiscalCalFormData &&
                            fiscalCalFormData.extraWeekYear &&
                            fiscalCalFormData.extraWeekYear[`${year.value}`] !== undefined,
                        })),
                        formState: fiscalCalFormData,
                        formSetter: updateFiscalCalSettingsState,
                        formPath: "extraWeekYear",
                        onChangeHelper: extraWeekOnChangeHelper as OnChangeHelper,
                        checkOptions: value =>
                          fiscalCalFormData && fiscalCalFormData.extraWeekYear
                            ? R.keys(fiscalCalFormData.extraWeekYear).join(", ")
                            : "",
                      },
                      {
                        inputType: NestedInputType.DROPDOWN,
                        label: "Fiscal month with an extra week:",
                        dropdownProps: {
                          type: DropdownToggleType.OUTLINED,
                          design: "primary",
                        },
                        disable:
                          !patternActiveSeedComplete ||
                          !fiscalCalFormData ||
                          !fiscalCalFormData.extraWeekYear ||
                          R.keys(fiscalCalFormData.extraWeekYear).length === 0,
                        options: patternActiveSeedComplete
                          ? new Array(12).fill(null).map((el, i) => ({
                              label: `${i + 1}. ${getMonthName(
                                Dfns.getMonth(
                                  Dfns.addMonths(
                                    i + (fiscalCalFormData.monthLabelCurrent ? 0 : 1),
                                    Dfns.parseISO(fiscalCalFormData.seedDate)
                                  )
                                )
                              )}`,
                              value: `${i}`,
                            }))
                          : [],
                        formState: fiscalCalFormData,
                        formPath: "extraWeekYear",
                        formSetter: updateFiscalCalSettingsState,
                        onChangeHelper: ((
                          val: number,
                          state: FiscalCalFormState
                        ): FiscalCalFormState => {
                          if (!state.extraWeekYear) {
                            state.extraWeekYear = {};
                          }
                          for (const year of R.keys(state.extraWeekYear)) {
                            state.extraWeekYear[year] = Number(val);
                          }
                          return state;
                        }) as OnChangeHelper,
                        checkOptions: () => {
                          return fiscalCalFormData && patternActiveSeedComplete
                            ? allEqualAndNotNil(fiscalCalFormData.extraWeekYear)
                              ? `${
                                  R.values(
                                    fiscalCalFormData.extraWeekYear as { [key: string]: number }
                                  )[0]
                                }`
                              : "11"
                            : "";
                        },
                      },
                    ],
                    true: [
                      {
                        inputType: NestedInputType.CUSTOM,
                        formPath: "extraWeekYear",
                        formState: fiscalCalFormData,
                        customContent: (
                          <div className="extraWeeksQuestions">
                            <div className="extraWeeksLabels">
                              <span>Year:</span>
                              <span>Extra week in:</span>
                            </div>
                            <Question
                              type={QuestionCardType.CHECKBOX}
                              options={EXTRA_WEEK_YEAR_OPTIONS}
                              onChange={updateFiscalCalSettingsState}
                              formState={fiscalCalFormData}
                              formStateKey={"extraWeekYear"}
                              alwasyShowNestedQuestion={true}
                              nestedOptionsConfig={
                                patternActiveSeedComplete
                                  ? (EXTRA_WEEK_YEAR_OPTIONS.reduce((acc, cv) => {
                                      if (
                                        fiscalCalFormData &&
                                        fiscalCalFormData.extraWeekYear &&
                                        fiscalCalFormData.extraWeekYear[`${cv.value}`] !== undefined
                                      ) {
                                        acc[cv.label] = [
                                          {
                                            inputType: "dropdown",
                                            options: new Array(12).fill(null).map((el, i) => ({
                                              label: `${i + 1}. ${getMonthName(
                                                Dfns.getMonth(
                                                  Dfns.addMonths(
                                                    i +
                                                      (fiscalCalFormData.monthLabelCurrent ? 0 : 1),
                                                    Dfns.parseISO(fiscalCalFormData.seedDate)
                                                  )
                                                )
                                              )}`,
                                              value: `${i}`,
                                            })),
                                            formPath: "extraWeekYear",
                                            dropdownProps: {
                                              size: "sm",
                                              design: "secondary",
                                            },
                                            onChangeHelper: (
                                              val: number,
                                              state: FiscalCalFormState
                                            ): FiscalCalFormState => {
                                              if (!state.extraWeekYear) {
                                                state.extraWeekYear = {};
                                              }
                                              state.extraWeekYear[`${cv.label}`] = Number(val);
                                              return state;
                                            },
                                            checkOptions: () => {
                                              return fiscalCalFormData &&
                                                fiscalCalFormData.extraWeekYear &&
                                                fiscalCalFormData.extraWeekYear[cv.label] !==
                                                  undefined
                                                ? `${fiscalCalFormData.extraWeekYear[cv.label]}`
                                                : "11";
                                            },
                                          },
                                        ];
                                      }
                                      return acc;
                                    }, {}) as NestedOptionsConfigMap)
                                  : undefined
                              }
                              checkOption={value =>
                                Boolean(
                                  fiscalCalFormData &&
                                    fiscalCalFormData.extraWeekYear &&
                                    fiscalCalFormData.extraWeekYear[value] !== undefined
                                )
                              }
                              onChangeHelper={extraWeekOnChangeHelper as OnChangeHelper}
                            />
                          </div>
                        ),
                      },
                    ],
                  }}
                />
                <div className="nestedInputNote">
                  {"Important:"}
                  <ul>
                    <li key={"extraWeekTipImportant"}>
                      {
                        "Include years before your current fiscal year start date for reporting accuracy."
                      }
                    </li>
                  </ul>
                  {"Tip:"}
                  <ul>
                    {EXTRA_WEEK_TIPS.map((tip, i) => (
                      <li key={`extraWeekTip${i}`}>{tip}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </QuestionCard>
        </ChartContainer>
      </Page>
    </div>
  );
};
